import React, { Fragment, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { PowerTable, Pagination, NoInformationFound } from 'Components';
import { cleanSearchParams, urlSearchToObject } from 'helpers/urlSearchParams';
import AdditionTab from '../AdditionTab/AdditionTab';
import './JobsDesktop.css';
import JobsDesktopHeader from './JobsDesktopHeader/JobsDesktopHeader';
import JobsDesktopSearchHeader from './JobsDesktopSearchHeader/JobsDesktopSearchHeader';
import SplitReportTab from '../SplitReportTab/SplitReportTab';

const JobsDesktop = (props) => {
  const showNoInformationFound = !props.data.isLoading && props.data.data.length === 0;
  const showNoInformationFoundSamples =
    !props.samples.isLoading && props.samples.data.length === 0 && props.globalSearchIndex === 1;

  const findKeyPairs = {
    0: 'receivedJobs',
    1: 'submittedJobsKeyPairs',
    3: 'splitReport',
  };

  const isEnvironmentalUser = props.userInfo.LOB === 'E';
  const isFoodUser = props.userInfo.LOB === 'F';
  let userAllowedSplitReportsAccess = props.userAllowedSplitReportsAccess;

  //! Split Report Tab Display conditional flag:
  const shouldDisplaySplitReportTab =
    userAllowedSplitReportsAccess &&
    ((isEnvironmentalUser && props.data.tabIndex === 3) || (!isEnvironmentalUser && props.data.tabIndex === 2));

  //! if the user is environmental user, and the tabIndex == 2 or the globalSearchIndex ==2, then display the addition tab:
  const shouldDisplayAdditionTab = isEnvironmentalUser && (props.data.tabIndex === 2 || props.globalSearchIndex === 2);

  const getPaymentStatus = (status, prepiadStatus) => {
    let translateKey;
    switch (prepiadStatus) {
      case 'D':
        translateKey = i18n.t('jobs:Draft');
        break;
      case 'E':
        translateKey = i18n.t('jobs:Pending');
        break;
      case 'P':
        translateKey = i18n.t('jobs:Paid');
        break;
      case 'C':
        translateKey = i18n.t('jobs:Cancelled');
        break;
      case 'O':
        translateKey = i18n.t('jobs:Overridden');
        break;
      default:
        break;
    }
    return `${status}/${translateKey}`;
  };

  const jobs = useMemo(() => {
    return (props.data.data || []).map((item) => ({
      ...item,
      UserLOB: props.userInfo.LOB,
      IsInternalUser: props.userInfo.IsInternalUser,
      StatusName: item.RequestStatus === '' ? item.StatusName : getPaymentStatus(item.StatusName, item.RequestStatus),
      Type: item.IsPrepaidOrder ? i18n.t('jobs:Order') : i18n.t('jobs:Job'),
    }));
  }, [props.data.data]);

  const isSampleDisplaying = props.globalSearchIndex === 1;

  const paginationProps = {
    onClick: props.onPaginationClick,
    currentPage: isSampleDisplaying ? props.samples.pageNumber : props.data.pageNumber,
    itemsPerPage: isSampleDisplaying ? props.samples.pageSize : props.data.pageSize,
    totalItems: isSampleDisplaying ? props.samples.totalItems : props.data.totalItems,
  };

  const [additionTabPageSize, setAdditionTabPageSize] = useState(25);
  const [splitReportTabPageSize, setSplitReportTabPageSize] = useState(25);

  const handlePageSizeChange = (e) => {
    if (props.data.tabIndex === 2 && isEnvironmentalUser) {
      setAdditionTabPageSize(e.target.value);
    } else if (
      (props.data.tabIndex === 2 && !isEnvironmentalUser) ||
      (props.data.tabIndex === 3 && isEnvironmentalUser)
    ) {
      setSplitReportTabPageSize(e.target.value);
    } else {
      props.onPageSizeChange(e);
    }
  };

  return (
    <div className="jobs-desktop">
      {props.isGlobalSearch ? (
        <JobsDesktopSearchHeader
          index={props.globalSearchIndex}
          tabs={props.globalSearchTabs}
          onTabsClick={(i) => props.setPageState({ globalSearchIndex: i })}
          showNoInformationFound={showNoInformationFound}
          onSearchDownload={props.onSearchDownload}
        />
      ) : (
        <JobsDesktopHeader
          tabs={props.tabs}
          tabIndex={props.data.tabIndex}
          onTabsClick={props.handleTabClick}
          onPageSizeChange={handlePageSizeChange}
          onUpload={props.onUpload}
          showNoInformationFound={showNoInformationFound}
          isSubmissionWarningON={props.isSubmissionWarningON}
          deleteSelectedItems={props.deleteSelectedItems}
        />
      )}

      {/* TODO: Please clean and modularize this page */}
      {shouldDisplayAdditionTab ? (
        <AdditionTab
          setAppState={props.setAppState}
          additionTabKeyPairs={props.additionTabKeyPairs}
          history={props.history}
          additionTabPageSize={additionTabPageSize}
          onAPIError={props.onAPIError}
          searchParams={cleanSearchParams(urlSearchToObject(props.location.search))}
        />
      ) : shouldDisplaySplitReportTab ? (
        <SplitReportTab
          appState={props.appState}
          setAppState={props.setAppState}
          pageSize={splitReportTabPageSize}
          setPageSize={setSplitReportTabPageSize}
          IsInternalUser={props.userInfo.IsInternalUser}
          clientNumber={props.userInfo.ClientNumber}
          userAllowedSplitReports={userAllowedSplitReportsAccess}
        />
      ) : (
        <Fragment>
          {props.globalSearchIndex === 0 && (
            <Fragment>
              {!showNoInformationFound && (
                <PowerTable
                      {...props.data}
                      data={jobs}
                      keys={props[findKeyPairs[props.data.tabIndex]].keys}
                      keyPairs={props[findKeyPairs[props.data.tabIndex]].keyPairs}
                      tabIndex={props.data.tabIndex}
                      popoverRef={props.popoverRef}
                      powerTableIndex={props.powerTableIndex}
                      powerTableCode={props.powerTableCode}
                      onOptionClick={props.onOptionClick}
                      viewCode={props[findKeyPairs[props.data.tabIndex]].viewCode}
                      appState={props.appState}
                      setPageState={props.setPageState}
                      setAppState={props.setAppState}
                      menuOptions={props.menuOptions}
                      rowRules={props.rowRules}
                      isSupplyWarningON={props.isSupplyWarningON}
                      isTemplateWarningON={props.isTemplateWarningON}
                      isSubmissionWarningON={props.isSubmissionWarningON}
                      isPrepaid={props.isPrepaid}
                />
              )}
              {props.isGlobalSearch && showNoInformationFound && (
                <NoInformationFound titleText={i18n.t('jobs:NoResults')} subText={i18n.t('jobs:NoResultsBody')} />
              )}
            </Fragment>
          )}

          {!props.isGlobalSearch && showNoInformationFound && (
            <NoInformationFound
              titleText={i18n.t('jobs:NoJobs')}
              subText={i18n.t('jobs:NewJob1')}
              linkText={i18n.t('jobs:NewJob')}
              link="/submissions/new"
            />
          )}

          {isSampleDisplaying && (
            <Fragment>
              {!showNoInformationFoundSamples && (
                <div className="jobs-desktop__table">
                  <PowerTable
                    {...props.samples}
                    viewCode="samples"
                    setAppState={props.setAppState}
                    menuOptions={props.menuOptions}
                    rowRules={props.rowRulesSample}
                    setPageState={props.setPageState}
                    onOptionClick={(code, i) => props.onOptionClick(code, i, true)}
                    powerTableIndex={props.powerTableIndex}
                    powerTableCode={props.powerTableCode}
                    isSupplyWarningON={props.isSupplyWarningON}
                    isTemplateWarningON={props.isTemplateWarningON}
                    isSubmissionWarningON={props.isSubmissionWarningON}
                  />
                </div>
              )}
              {props.isGlobalSearch && showNoInformationFoundSamples && (
                <NoInformationFound titleText={i18n.t('jobs:NoResults')} subText={i18n.t('jobs:NoResultsBody')} />
              )}
            </Fragment>
          )}
          {!(showNoInformationFoundSamples || showNoInformationFound) && (
            <div className="jobs-desktop__pagination">
              <Pagination {...paginationProps} />
            </div>
          )}
        </Fragment>
      )}
    </div>
  );
};

JobsDesktop.propTypes = {
  popoverRef: PropTypes.shape({}),
  popoverRow: PropTypes.number,
  powerTableIndex: PropTypes.number,
  onPageSizeChange: PropTypes.func,
  data: PropTypes.shape({
    pageSize: PropTypes.number,
    totalItems: PropTypes.number,
    isLoading: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    tabIndex: PropTypes.number,
    pageNumber: PropTypes.number,
  }),
  isGlobalSearch: PropTypes.bool,
  onAuditCancel: PropTypes.func,
  onAuditProceed: PropTypes.func,
  onPaginationClick: PropTypes.func,
  setAppState: PropTypes.func,
  onDocumentClick: PropTypes.func,
  ChangeLogComments: PropTypes.string,
  onUpload: PropTypes.func,
  setPageState: PropTypes.func,
  globalSearchIndex: PropTypes.number,
  globalSearchTabs: PropTypes.arrayOf(PropTypes.string),
  tabs: PropTypes.arrayOf(PropTypes.string),
  pageSizes: PropTypes.arrayOf(PropTypes.shape({})),
  handleTabClick: PropTypes.func,
  jobFiles: PropTypes.shape({}),
  onOptionClick: PropTypes.func,
  onSearchDownload: PropTypes.func,
  samples: PropTypes.shape({
    pageSize: PropTypes.number,
    totalItems: PropTypes.number,
    isLoading: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    tabIndex: PropTypes.number,
    pageNumber: PropTypes.number,
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  history: PropTypes.shape({}),
  powerTableCode: PropTypes.string,
  menuOptions: PropTypes.arrayOf(PropTypes.shape({})),
  rowRules: PropTypes.func,
  rowRulesSample: PropTypes.func,
  additionTabKeyPairs: PropTypes.shape({}),
  splitReportTabKeyPairs: PropTypes.shape({}),
  onAPIError: PropTypes.func,
  userInfo: PropTypes.shape({
    LOB: PropTypes.string,
    IsInternalUser: PropTypes.bool,
  }),
  isSupplyWarningON: PropTypes.bool,
  isTemplateWarningON: PropTypes.bool,
  isSubmissionWarningON: PropTypes.bool,
};

export default JobsDesktop;



// WEBPACK FOOTER //
// ./src/containers/Jobs/JobsDesktop/JobsDesktop.jsx